import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { FaEdit, FaKey, FaLock, FaPlus, FaTrash, FaUnlock, FaUserShield } from "react-icons/fa";
import API from "../../api/api";

const emptyForm = { name: "", mobile: "", email: "", password: "", is_blocked: false };

const CampManagerManagement = ({ campId }) => {
  const [managers, setManagers] = useState([]);
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadManagers = useCallback(async () => {
    if (!campId) return;
    setLoading(true);
    try {
      const [response, eligibleResponse] = await Promise.all([
        API.camp.getCampManagers({ camp_id: campId }),
        API.camp.getEligibleCampManagers({ camp_id: campId }),
      ]);
      setManagers(response?.data?.managers || response?.managers || []);
      setEligibleManagers(eligibleResponse?.data?.managers || eligibleResponse?.managers || []);
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Unable to load manager roles.", "error");
    } finally {
      setLoading(false);
    }
  }, [campId]);

  useEffect(() => { loadManagers(); }, [loadManagers]);

  const reset = () => { setEditingId(null); setForm(emptyForm); };
  const edit = (manager) => {
    setEditingId(manager.id);
    setForm({
      name: manager.name || "",
      mobile: manager.mobile || "",
      email: manager.email || "",
      password: "",
      is_blocked: Boolean(manager.camp_is_blocked ?? manager.is_blocked),
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, camp_id: campId };
      if (editingId && !payload.password) delete payload.password;
      if (editingId) await API.camp.updateCampManager(editingId, payload);
      else await API.camp.createCampManager(payload);
      Swal.fire({ icon: "success", title: editingId ? "Manager Updated" : "Manager Login Created", timer: 1500, showConfirmButton: false });
      reset();
      loadManagers();
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Unable to save manager login.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (manager) => {
    const currentlyDisabled = Boolean(manager.camp_is_blocked ?? manager.is_blocked);
    const result = await Swal.fire({
      title: `${currentlyDisabled ? "Enable" : "Disable"} ${manager.name}'s camp login?`,
      text: manager.appointment_scope === "regular"
        ? "This changes only this camp appointment. The regular manager login is managed separately in Login Management."
        : "This changes access to the camp manager portal.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: currentlyDisabled ? "Enable Login" : "Disable Login",
      confirmButtonColor: currentlyDisabled ? "#059669" : "#dc2626",
    });
    if (!result.isConfirmed) return;
    try {
      await API.camp.updateCampManager(manager.id, { camp_id: campId, is_blocked: !currentlyDisabled });
      await loadManagers();
      Swal.fire({ icon: "success", title: currentlyDisabled ? "Camp Login Enabled" : "Camp Login Disabled", timer: 1300, showConfirmButton: false });
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Unable to change manager login status.", "error");
    }
  };

  const remove = async (manager) => {
    const result = await Swal.fire({
      title: `Delete ${manager.name}'s manager login?`,
      text: "This removes only the Manager login role; camp attendance records remain preserved.",
      icon: "warning", showCancelButton: true, confirmButtonText: "Delete Login", confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;
    await API.camp.deleteCampManager(manager.id, { camp_id: campId });
    if (editingId === manager.id) reset();
    loadManagers();
  };

  const enrollRegularManager = async () => {
    if (!selectedManagerId) return;
    try {
      await API.camp.enrollCampManager({ camp_id: campId, user_id: Number(selectedManagerId) });
      setSelectedManagerId("");
      Swal.fire({ icon: "success", title: "Manager Enrolled", timer: 1400, showConfirmButton: false });
      loadManagers();
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Unable to enroll manager.", "error");
    }
  };

  return (
    <section className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-cyan-50 p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-bold text-slate-900"><FaUserShield className="text-indigo-700" /> Camp Manager Roles & Logins</h3>
          <p className="text-xs text-slate-500">Enroll a regular Manager, or create a login that exists only for this camp.</p>
        </div>
        <span className="rounded-full border border-indigo-200 bg-indigo-100 px-2.5 py-1 text-[10px] font-bold text-indigo-800">{managers.length} Managers</span>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(320px,0.85fr)_minmax(420px,1.15fr)]">
        <div className="space-y-3">
        <div className="rounded-xl border border-indigo-200 bg-white p-3">
          <h4 className="mb-2 text-sm font-bold">Enroll Regular Manager</h4>
          <div className="flex gap-2">
            <select value={selectedManagerId} onChange={(e) => setSelectedManagerId(e.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-xs">
              <option value="">Select from regular directory</option>
              {eligibleManagers.map((manager) => <option key={manager.id} value={manager.id}>{manager.name} - {manager.mobile}</option>)}
            </select>
            <button type="button" onClick={enrollRegularManager} disabled={!selectedManagerId} className="rounded-lg bg-indigo-700 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">Enroll</button>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between"><h4 className="text-sm font-bold">{editingId ? "Edit Camp Manager" : "Create Camp-only Manager"}</h4>{editingId && <button type="button" onClick={reset} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Cancel edit</button>}</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input required placeholder="Manager name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-xs" />
            <input required placeholder="Mobile / login ID" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-xs" />
            <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-xs" />
            <input required={!editingId} type="password" minLength="6" placeholder={editingId ? "New password (leave blank to keep)" : "Password (minimum 6)"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-lg border border-slate-300 px-3 py-2 text-xs" />
          </div>
          <button disabled={saving || !campId} className="inline-flex items-center gap-2 rounded-lg bg-indigo-700 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">{editingId ? <FaEdit /> : <FaPlus />} {saving ? "Saving..." : editingId ? "Save Changes" : "Create Manager Login"}</button>
        </form>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto"><table className="min-w-full text-xs"><thead className="bg-slate-50 text-slate-600"><tr><th className="p-2.5 text-left">Manager</th><th className="p-2.5 text-left">Login</th><th className="p-2.5 text-center">Status</th><th className="p-2.5 text-right">Actions</th></tr></thead><tbody>
            {loading ? <tr><td colSpan={4} className="p-6 text-center text-slate-500">Loading manager roles...</td></tr> : managers.length === 0 ? <tr><td colSpan={4} className="p-6 text-center text-slate-500">No Manager login created for this camp.</td></tr> : managers.map((manager) => (
              <tr key={manager.id} className="border-t border-slate-100">
                <td className="p-2.5"><div className="font-bold text-slate-900">{manager.name}</div><div className="text-[10px] text-indigo-600">{manager.appointment_scope === "regular" ? "Regular Manager - Camp Appointment" : "Camp-only Manager"}</div></td>
                <td className="p-2.5"><div>{manager.mobile}</div><div className="text-[10px] text-slate-400">{manager.email || "No email"}</div></td>
                <td className="p-2.5 text-center"><span className={`rounded-full px-2 py-1 text-[10px] font-bold ${(manager.camp_is_blocked ?? manager.is_blocked) ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{(manager.camp_is_blocked ?? manager.is_blocked) ? "Disabled" : "Active"}</span></td>
                <td className="p-2.5 text-right whitespace-nowrap"><button type="button" onClick={() => toggle(manager)} title={(manager.camp_is_blocked ?? manager.is_blocked) ? "Enable camp login" : "Disable camp login"} className={`mr-1 inline-flex items-center gap-1 rounded border px-2 py-1.5 text-[10px] font-bold ${(manager.camp_is_blocked ?? manager.is_blocked) ? "border-emerald-200 text-emerald-700" : "border-amber-200 text-amber-700"}`}>{(manager.camp_is_blocked ?? manager.is_blocked) ? <FaUnlock /> : <FaLock />}{(manager.camp_is_blocked ?? manager.is_blocked) ? "Enable" : "Disable"}</button><button type="button" onClick={() => edit(manager)} title="Edit manager or change password" className="mr-1 rounded border border-indigo-200 p-1.5 text-indigo-700"><FaKey /></button><button type="button" onClick={() => remove(manager)} title="Delete manager login" className="rounded border border-rose-200 p-1.5 text-rose-700"><FaTrash /></button></td>
              </tr>
            ))}
          </tbody></table></div>
        </div>
      </div>
    </section>
  );
};

export default CampManagerManagement;
